import { castToBoolean } from '../util';

let programCount = 0;
let programs = [];

const MS_PER_DAY = 24 * 60 * 60 * 1000;

const startOfDay = (date) => new Date(date.getFullYear(), date.getMonth(), date.getDate());

const toMinutes = (value) => parseInt(value, 10) || 0;

class Program {
  constructor() {
    const data = arguments[0] || {};
    this.staff_can_create_locations         = castToBoolean(data.staff_can_create_locations);
    this.staff_can_select_from_my_locations = castToBoolean(data.staff_can_select_from_my_locations);
    this.staff_can_search_locations         = castToBoolean(data.staff_can_search_locations);
    this.staff_can_search_nearby_locations  = castToBoolean(data.staff_can_search_nearby_locations);
    this.staff_can_select_from_my_territory = castToBoolean(data.staff_can_select_from_my_territory);
    this.min_event_minutes = Number(data.min_event_minutes) ?? 0;
    this.max_event_minutes = Number(data.max_event_minutes) ?? 0;

    delete(data.staff_can_create_locations);
    delete(data.staff_can_select_from_my_locations);
    delete(data.staff_can_search_locations);
    delete(data.staff_can_search_nearby_locations);
    delete(data.staff_can_select_from_my_territory);
    delete(data.min_event_minutes);
    delete(data.max_event_minutes);

    for (let key in data) {
      this[key] = data[key];
    }

    this._program_count = programCount;
    programCount += 1;
    this._instantiated_at = (new Date()).toISOString();

    programs.push({
      id: this.id,
      instantiated_at: this._instantiated_at,
      count: this._program_count,
      program: this,
    });
  }

  /**
   * An Event Schedule setting counts only when the server resolved it enabled
   * and it still carries a message and a positive day count. An absent key means
   * not configured, which is also how an older backend reads.
   */
  _activeScheduleSetting(key) {
    const setting = this[key];
    if (!setting || !setting.enabled || !setting.message) {
      return null;
    }

    const days = parseInt(setting.days, 10);

    return days > 0 ? { days, message: setting.message } : null;
  }

  /**
   * Whole calendar days, so a date exactly N days out counts as N. Rounding
   * absorbs the 23 and 25 hour days either side of a DST change.
   */
  _calendarDaysFromToday(date) {
    if (!date) {
      return null;
    }

    const parsed = date instanceof Date ? date : new Date(date);
    if (isNaN(parsed.getTime())) {
      return null;
    }

    return Math.round((startOfDay(parsed) - startOfDay(new Date())) / MS_PER_DAY);
  }

  _scheduleMessageIfTooSoon(key, date) {
    const setting = this._activeScheduleSetting(key);
    if (!setting) {
      return null;
    }

    const days = this._calendarDaysFromToday(date);
    if (days === null) {
      return null;
    }

    return days < setting.days ? setting.message : null;
  }

  /** Blocks the save. */
  eventScheduleRuleViolation(date) {
    return this._scheduleMessageIfTooSoon('event_schedule_rule', date);
  }

  /**
   * First date the Event Schedule Rule allows, for greying out the days before
   * it in a date picker. Null when no rule applies.
   */
  earliestScheduleRuleDate() {
    const setting = this._activeScheduleSetting('event_schedule_rule');
    if (!setting) {
      return null;
    }

    const earliest = startOfDay(new Date());
    earliest.setDate(earliest.getDate() + setting.days);

    return earliest;
  }

  /** Advisory only: the date stays saveable. */
  eventScheduleWarning(date) {
    return this._scheduleMessageIfTooSoon('event_schedule_warning', date);
  }

  /**
   * Event Time Rule limits in minutes. The level that owns them owns both, so
   * the selected event type is consulted only where the program leaves min and
   * max at 0. Zero means no limit at either level.
   *
   * @param {?Object} eventType the selected events_types row
   *
   * @returns {{min: number, max: number}}
   */
  eventDurationLimits(eventType) {
    const programLimits = {
      min: toMinutes(this.min_event_minutes),
      max: toMinutes(this.max_event_minutes),
    };

    if (programLimits.min > 0 || programLimits.max > 0) {
      return programLimits;
    }

    return {
      min: toMinutes(eventType && eventType.min_event_minutes),
      max: toMinutes(eventType && eventType.max_event_minutes),
    };
  }
}

export default Program;
